import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
} from "@mui/material";
import { useAddTransaction, useCategories } from "../hooks/useApi";
import PropTypes from "prop-types";
import { RESPONSIBLE_PEOPLE } from "../utils/people";

const AddTransactionDialog = ({ open, onClose }) => {
  const [date, setDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [person, setPerson] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState(null);

  const { data: categories = [] } = useCategories();
  const addTransactionMutation = useAddTransaction();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!date || !description || !category || !amount) {
      setError("All fields except sub‑category are required.");
      return;
    }
    const amountNumber = Number(amount);
    if (isNaN(amountNumber) || amountNumber <= 0) {
      setError("Amount must be a positive number.");
      return;
    }

    try {
      await addTransactionMutation.mutateAsync({
        date,
        description,
        amount: amountNumber,
        final_category: category || "Uncategorized",
        sub_category: subCategory || null,
        responsible_person: person || null,
      });
      // Reset form and close dialog on success
      setDate(new Date().toISOString().split("T")[0]);
      setDescription("");
      setCategory("");
      setSubCategory("");
      setPerson("");
      setAmount("");
      onClose();
    } catch (err) {
      setError(err.message ?? "Failed to add transaction.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Transaction</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <form onSubmit={handleSubmit} id="add-transaction-form">
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            margin="dense"
            required
          />
          <FormControl fullWidth margin="dense" required>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              label="Category"
            >
              {categories.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Sub‑Category"
            value={subCategory}
            onChange={(e) => setSubCategory(e.target.value)}
            fullWidth
            margin="dense"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="person-label">Person</InputLabel>
            <Select
              labelId="person-label"
              value={person}
              onChange={(e) => setPerson(e.target.value)}
              label="Person"
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {RESPONSIBLE_PEOPLE.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            margin="dense"
            required
            inputProps={{ step: "0.01", min: "0" }}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={addTransactionMutation.isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-transaction-form"
          variant="contained"
          color="primary"
          disabled={addTransactionMutation.isPending}
        >
          {addTransactionMutation.isPending ? "Adding..." : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTransactionDialog;

AddTransactionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
